// Delivery-service resolution. Rules are data (editable in Firestore via the
// Settings screen); these DEFAULTS are the fallback when no config doc exists.
//
// - Etsy has no named service -> infer from the shipping price via "ladders"
//   (base + step per extra item, up to maxExtras). Editable because Kate changes
//   her postage prices.
// - eBay / Shopify carry a named method -> map it to a canonical service. Editable
//   because the platform service names drift over time.

import { parseMoney } from './clean';

export const DEFAULT_DELIVERY_RULES = {
  etsy: {
    zeroService: 'Tracked 48', // price == 0
    ladders: [
      { service: 'Tracked 24', base: 3.0, step: 0.5, maxExtras: 5 },
      { service: 'SPECIAL DELIVERY', base: 7.0, step: 0.5, maxExtras: 5 },
      { service: "INT'L TRACKED", base: 6.65, step: 0.5, maxExtras: 5 },
      { service: "INT'L TRACKED", base: 10.65, step: 1.5, maxExtras: 5 },
    ],
    fallback: 'Unknown',
  },
  ebay: {
    nameMap: {
      'royal mail 2nd class signed for': 'Tracked 48',
      'royal mail special delivery (tm) 1:00 pm': 'Special Delivery',
      'royal mail 1st class signed for': 'Tracked 24',
      'royal mail international tracked': "Int'l Tracked",
      'royal mail international standard': "Standard Int'l (???)",
      'royal mail tracked 48': 'Tracked 48',
      'royal mail tracked 24': 'Tracked 24',
      'royal mail special delivery': 'Special Delivery',
      'royal mail special delivery 1pm': 'Special Delivery',
    },
  },
  shopify: {
    nameMap: {
      standard: 'Tracked 48',
      'special delivery (uk only)': 'Special Delivery',
      express: 'Tracked 24',
      'express international': "Int'l Tracked",
      'royal mail tracked 48': 'Tracked 48',
      'royal mail tracked 24': 'Tracked 24',
      'royal mail special delivery': 'Special Delivery',
      'royal mail international tracked': "Int'l Tracked",
    },
  },
};

/** Does `price` equal base + step*n for some n in 0..maxExtras? (float-tolerant) */
function isPriceInLadder(price, base, step, maxExtras) {
  const b = Number(base);
  const s = Number(step) || 0;
  const max = Number(maxExtras) || 0;
  for (let i = 0; i <= max; i++) {
    if (Math.abs(price - (b + s * i)) < 0.000001) return true;
  }
  return false;
}

/** Etsy: infer the service from the shipping price using the (editable) ladders. */
export function etsyDeliveryService(shippingValue, rules = DEFAULT_DELIVERY_RULES.etsy) {
  const r = rules || DEFAULT_DELIVERY_RULES.etsy;
  const price = parseMoney(shippingValue);
  if (price === 0) return r.zeroService || 'Tracked 48';
  for (const l of r.ladders || []) {
    if (isPriceInLadder(price, l.base, l.step, l.maxExtras)) return l.service;
  }
  return r.fallback || 'Unknown';
}

/** eBay: map the "Delivery service" string via the (editable) name map. */
export function ebayDeliveryService(value, rules = DEFAULT_DELIVERY_RULES.ebay) {
  if (!value) return '';
  const map = (rules && rules.nameMap) || DEFAULT_DELIVERY_RULES.ebay.nameMap;
  return map[String(value).trim().toLowerCase()] || value;
}

/** Shopify: map the "Shipping Method" string via the (editable) name map. */
export function shopifyDeliveryService(value, rules = DEFAULT_DELIVERY_RULES.shopify) {
  if (!value) return '';
  const map = (rules && rules.nameMap) || DEFAULT_DELIVERY_RULES.shopify.nameMap;
  return map[String(value).trim().toLowerCase()] || value;
}
